import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEnvPrefix } from '../hooks/useEnvNavigation';
import { useAuth } from '../contexts/AuthContext';
import { submissionsAPI } from '../services/api';
import api from '../services/api';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ClipboardCheck, Search, Plus, Eye, Download, RefreshCw,
  CheckCircle2, Clock, XCircle, AlertCircle, FileText,
  AlertTriangle, Truck, Upload, RotateCcw, Trash2
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const downloadFile = async (url) => {
  try {
    const response = await axios.get(url, { responseType: 'blob' });
    const disposition = response.headers['content-disposition'];
    let filename = 'download';
    if (disposition) {
      const match = disposition.match(/filename=([^;]+)/);
      if (match) filename = match[1].replace(/"/g, '');
    }
    const blob = new Blob([response.data]);
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);
  } catch (e) {
    toast.error('Download failed');
  }
};

const viewFile = async (url) => {
  try {
    const response = await axios.get(url, { responseType: 'blob' });
    const blob = new Blob([response.data], { type: response.headers['content-type'] });
    const viewUrl = window.URL.createObjectURL(blob);
    window.open(viewUrl, '_blank');
  } catch (e) {
    toast.error('Could not open file');
  }
};

const statusConfig = {
  draft: { label: 'Draft', className: 'bg-slate-100 text-slate-700', icon: FileText },
  submitted: { label: 'Submitted', className: 'bg-amber-100 text-amber-700', icon: Clock },
  returned: { label: 'Returned', className: 'bg-blue-100 text-blue-700', icon: AlertCircle },
  approved: { label: 'Approved', className: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  rejected: { label: 'Rejected', className: 'bg-red-100 text-red-700', icon: XCircle },
  closed: { label: 'Closed', className: 'bg-slate-100 text-slate-600', icon: CheckCircle2 },
};

export default function DailyInspectionsPage() {
  const navigate = useNavigate();
  const envPrefix = useEnvPrefix();
  const { isAdmin } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [drivers, setDrivers] = useState({});
  const [templateFields, setTemplateFields] = useState([]);
  const [inspectionCAs, setInspectionCAs] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showUploadDoc, setShowUploadDoc] = useState(null);
  const [docFile, setDocFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [subRes, drvRes, tplRes, caRes] = await Promise.all([
        submissionsAPI.list({ template_code: 'F004' }),
        api.get('/drivers'),
        api.get('/templates/F004'),
        api.get('/corrective-actions'),
      ]);
      setSubmissions(subRes.data);
      const drvMap = {};
      (drvRes.data || []).forEach(d => {
        const fn = d.first_name || d.user?.first_name || '';
        const ln = d.last_name || d.user?.last_name || '';
        drvMap[d.id] = `${fn} ${ln}`.trim() || d.employee_number || d.id;
      });
      setDrivers(drvMap);
      setTemplateFields(tplRes.data?.fields || []);

      // Group CAs by submission (related_entity_id)
      const caMap = {};
      (caRes.data || []).forEach(ca => {
        if (ca.source === 'daily_inspection' && ca.related_entity_id) {
          if (!caMap[ca.related_entity_id]) caMap[ca.related_entity_id] = [];
          caMap[ca.related_entity_id].push(ca);
        }
      });
      setInspectionCAs(caMap);
    } catch (e) {
      toast.error('Failed to load inspections');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getDriverName = (sub) => {
    const fd = sub.form_data || {};
    const driverId = fd.driver_name;
    if (drivers[driverId]) return drivers[driverId];
    return fd.driver_display_name || driverId || '-';
  };

  // Build a set of yes_no fields with their expected answers from the template
  const getYesNoExpectations = () => {
    const map = {};
    templateFields.forEach(f => {
      if (f.field_type === 'yes_no' && f.expected_answer) {
        map[f.name] = f.expected_answer;
      }
    });
    return map;
  };

  const getIssueCount = (sub) => {
    const fd = sub.form_data || {};
    const yesNoExpect = getYesNoExpectations();
    let issues = 0;
    Object.entries(fd).forEach(([key, val]) => {
      if (key.endsWith('_comment') || key.startsWith('section_')) return;
      // Inspection check items: "Needs Attention" is always an issue
      if (val === 'Needs Attention') { issues++; return; }
      // Yes/No items: only an issue if the answer contradicts expectation
      if (yesNoExpect[key]) {
        const expect = yesNoExpect[key];
        if ((expect === 'yes_required' && val === 'No') ||
            (expect === 'no_required' && val === 'Yes')) {
          issues++;
        }
      }
    });
    return issues;
  };

  const handleUploadDoc = async () => {
    if (!docFile || !showUploadDoc) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', docFile);
      await axios.post(`${API_URL}/api/daily-inspections/${showUploadDoc}/upload-document`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Document uploaded');
      setDocFile(null);
      setShowUploadDoc(null);
      fetchData();
    } catch (e) {
      toast.error('Failed to upload document');
    }
    setUploading(false);
  };

  const handleDeleteDoc = async (submissionId) => {
    if (!window.confirm('Delete the attached document? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/daily-inspections/${submissionId}/document`);
      toast.success('Document deleted');
      fetchData();
    } catch (e) {
      toast.error('Failed to delete document');
    }
  };

  const filtered = submissions
    .filter(s => {
      if (statusFilter !== 'all' && s.status !== statusFilter) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      const fd = s.form_data || {};
      const vehicle = fd.vehicle_registration || '';
      const driver = getDriverName(s);
      return s.submission_number.toLowerCase().includes(q) ||
        vehicle.toLowerCase().includes(q) ||
        driver.toLowerCase().includes(q) ||
        (fd.inspection_date || '').includes(q);
    })
    .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));

  const total = submissions.length;
  const approved = submissions.filter(s => s.status === 'approved').length;
  const pending = submissions.filter(s => s.status === 'submitted').length;
  const withIssues = submissions.filter(s => getIssueCount(s) > 0).length;

  if (loading) {
    return <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }

  return (
    <div data-testid="daily-inspections-page">
      <div className="flex items-center gap-3 mb-6">
        <ClipboardCheck className="w-7 h-7 text-slate-900" />
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
          DAILY VEHICLE INSPECTIONS
        </h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card><CardContent className="pt-4">
          <div className="text-sm text-slate-500">Total Inspections</div>
          <div className="text-2xl font-bold" data-testid="inspection-total">{total}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-sm text-slate-500">Approved</div>
          <div className="text-2xl font-bold text-emerald-600">{approved}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-sm text-slate-500">Pending Review</div>
          <div className="text-2xl font-bold text-amber-600">{pending}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-sm text-slate-500">With Issues</div>
          <div className="text-2xl font-bold text-red-600">{withIssues}</div>
        </CardContent></Card>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by vehicle, driver, date..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="max-w-xs"
            data-testid="inspection-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px]" data-testid="inspection-status-filter">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="submitted">Submitted</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="returned">Returned</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={() => navigate(`${envPrefix}/submissions/new/F004`)} data-testid="new-inspection-btn">
          <Plus className="w-4 h-4 mr-1" />New Inspection
        </Button>
        <Button variant="outline" onClick={() => downloadFile(`${API_URL}/api/daily-inspections/export/excel`)} data-testid="export-inspections-btn">
          <Download className="w-4 h-4 mr-1" />Export Excel
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Reference</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Vehicle</TableHead>
                <TableHead>Driver</TableHead>
                <TableHead>Odometer</TableHead>
                <TableHead className="text-center">Issues</TableHead>
                <TableHead>Corrective Action</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-center">Document</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(sub => {
                const fd = sub.form_data || {};
                const issues = getIssueCount(sub);
                const sc = statusConfig[sub.status] || statusConfig.draft;
                const hasDoc = !!sub.attached_document;
                const cas = inspectionCAs[sub.id] || [];
                return (
                  <TableRow key={sub.id} data-testid={`inspection-row-${sub.id}`}>
                    <TableCell className="font-mono text-sm font-medium">{sub.submission_number}</TableCell>
                    <TableCell>{fd.inspection_date || sub.created_at?.slice(0, 10)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <Truck className="w-4 h-4 text-slate-400" />
                        <span className="font-medium">{fd.vehicle_registration || '-'}</span>
                      </div>
                      {fd.trailer_1_registration && fd.trailer_1_registration !== 'none' && (
                        <span className="text-xs text-slate-500 ml-5.5">+ {fd.trailer_1_registration}</span>
                      )}
                    </TableCell>
                    <TableCell>{getDriverName(sub)}</TableCell>
                    <TableCell>{fd.odometer_reading ? Number(fd.odometer_reading).toLocaleString() : '-'}</TableCell>
                    <TableCell className="text-center">
                      {issues > 0 ? (
                        <Badge variant="destructive" className="gap-1">
                          <AlertTriangle className="w-3 h-3" />{issues}
                        </Badge>
                      ) : (
                        <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                          <CheckCircle2 className="w-3 h-3 mr-1" />OK
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {cas.length > 0 ? (
                        <div className="space-y-1 max-w-[200px]">
                          {cas.map(ca => (
                            <div key={ca.id}>
                              <Badge className={`text-xs ${
                                ca.status === 'open' ? 'bg-red-100 text-red-700' :
                                ca.status === 'in_progress' ? 'bg-amber-100 text-amber-700' :
                                ca.status === 'closed' ? 'bg-emerald-100 text-emerald-700' :
                                'bg-slate-100 text-slate-600'
                              }`}>
                                {ca.status === 'open' ? 'Open' : ca.status === 'in_progress' ? 'In Progress' : ca.status === 'closed' ? 'Closed' : ca.status}
                              </Badge>
                              <p className="text-xs text-slate-500 mt-0.5 line-clamp-2" title={ca.description}>
                                {ca.inspection_field ? ca.inspection_field.replace(/_/g, ' ') : ''}
                                {ca.resolution_notes ? ` - ${ca.resolution_notes}` : ''}
                              </p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={sc.className}>{sc.label}</Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      {hasDoc ? (
                        <div className="flex items-center justify-center gap-1">
                          <Button size="sm" variant="ghost" title="View Document" onClick={(e) => { e.stopPropagation(); viewFile(`${API_URL}/api/daily-inspections/${sub.id}/download-document`); }} data-testid={`view-doc-${sub.id}`}>
                            <Eye className="w-4 h-4 text-blue-600" />
                          </Button>
                          <Button size="sm" variant="ghost" title="Download Document" onClick={(e) => { e.stopPropagation(); downloadFile(`${API_URL}/api/daily-inspections/${sub.id}/download-document`); }} data-testid={`download-doc-${sub.id}`}>
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" title="Replace Document" onClick={(e) => { e.stopPropagation(); setShowUploadDoc(sub.id); setDocFile(null); }} data-testid={`replace-doc-${sub.id}`}>
                            <RotateCcw className="w-4 h-4 text-amber-600" />
                          </Button>
                          {isAdmin && (
                            <Button size="sm" variant="ghost" title="Delete Document" onClick={(e) => { e.stopPropagation(); handleDeleteDoc(sub.id); }} data-testid={`delete-doc-${sub.id}`}>
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          )}
                        </div>
                      ) : (
                        <Button size="sm" variant="ghost" title="Upload Document" onClick={(e) => { e.stopPropagation(); setShowUploadDoc(sub.id); setDocFile(null); }} data-testid={`upload-doc-${sub.id}`}>
                          <Upload className="w-4 h-4 text-slate-400" />
                        </Button>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <Button size="sm" variant="ghost" onClick={() => navigate(`${envPrefix}/submissions/${sub.id}`)}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} className="text-center text-slate-400 py-12">
                    No inspections found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Upload Document Dialog */}
      <Dialog open={!!showUploadDoc} onOpenChange={() => setShowUploadDoc(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Upload Inspection Document</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Document (PDF, Image, etc.)</Label><Input type="file" onChange={e => setDocFile(e.target.files[0])} data-testid="inspection-doc-input" /></div>
          </div>
          <DialogFooter><Button onClick={handleUploadDoc} disabled={uploading || !docFile} data-testid="upload-inspection-doc-btn">{uploading ? 'Uploading...' : 'Upload'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
